import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
    Divider,
} from "@mui/material";
import dayjs from "dayjs";

interface PaymentDetailsModalProps {
    open: boolean;
    onClose: () => void;
    transaction: any; // Using any for now since the interface might be outdated
}

export default function PaymentDetailsModal({ open, onClose, transaction }: PaymentDetailsModalProps) {
    if (!transaction) return null;

    const formatDate = (date: string | Date) => {
        if (!date) return "-";
        return dayjs(date).format("YYYY-MM-DD HH:mm");
    };

    const formatJson = (data: any) => {
        try {
            if (typeof data === "string") {
                const parsed = JSON.parse(data);
                return JSON.stringify(parsed, null, 2);
            }
            return JSON.stringify(data, null, 2);
        } catch {
            return typeof data === "string" ? data : JSON.stringify(data, null, 2);
        }
    };

    const getStatusColor = (status: string) => {
        const upperStatus = status?.toUpperCase();
        if (upperStatus === "SUCCESS" || upperStatus === "AUTHORIZED") return "success";
        if (upperStatus === "PENDING" || upperStatus === "INITIATED") return "warning";
        if (upperStatus === "FAILED" || upperStatus === "CANCELLED") return "error";
        return "default";
    };

    const getCycleColor = (cycle: string) => {
        const upperCycle = cycle?.toUpperCase();
        if (upperCycle === "TRIAL") return "info";
        if (upperCycle === "FIRST_RENEWAL") return "warning";
        if (upperCycle === "RECURRING") return "success";
        return "default";
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6" component="div">
                    Payment Details - Transaction #{transaction.id}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Basic Payment Information */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Payment Details
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Payment Method
                                </Typography>
                                <Typography variant="body1">{transaction.method || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Payment Source
                                </Typography>
                                <Typography variant="body1">{transaction.source || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Gateway Charge ID
                                </Typography>
                                <Typography variant="body1">{transaction.apiId || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    IP Country
                                </Typography>
                                <Typography variant="body1">{transaction.detail?.device?.location || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Card Last 4 Digits
                                </Typography>
                                <Typography variant="body1">
                                    {transaction.detail?.paymentMethod?.cardInfo?.last_four
                                        ? `**** **** **** ${transaction.detail.paymentMethod.cardInfo.last_four}`
                                        : "-"}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    <Divider />

                    {/* Subscription Context */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Subscription Context
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Subscription Cycle
                                </Typography>
                                <Chip
                                    label={transaction.subscription?.cycle || "-"}
                                    color={getCycleColor(transaction.subscription?.cycle)}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Next Billing Date
                                </Typography>
                                <Typography variant="body1">{formatDate(transaction.subscription?.endAt)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Subscription Amount
                                </Typography>
                                <Typography variant="body1">
                                    {transaction.subscription?.amount || "-"} {transaction.subscription?.currency || ""}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Source of Subscription
                                </Typography>
                                <Typography variant="body1">{transaction.source || "-"}</Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Financial Adjustments - Only show if coupon exists */}
                    {transaction.subscription?.coupon && (
                        <>
                            <Divider />
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Financial Adjustments
                                </Typography>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Coupon Code
                                        </Typography>
                                        <Typography variant="body1">
                                            {transaction.subscription.coupon.code || "-"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Amount Type
                                        </Typography>
                                        <Typography variant="body1">
                                            {transaction.subscription.coupon.group?.amountType || "-"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Amount Value
                                        </Typography>
                                        <Typography variant="body1">
                                            {transaction.subscription.coupon.group?.amountValue || "-"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </>
                    )}

                    <Divider />

                    {/* Lifecycle States */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Lifecycle States
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Status
                                </Typography>
                                <Chip
                                    label={transaction.status || "-"}
                                    color={getStatusColor(transaction.status)}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Transaction Status
                                </Typography>
                                <Chip
                                    label={transaction.data?.status || "-"}
                                    color={getStatusColor(transaction.data?.status)}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
